import React, { useEffect, useState } from "react";
import "./AddEmergencyContactModal.css";
import { titles } from "constants/titles";
import CustomButton from "components/widgets/custom-button/CustomButton";
import CustomInput from "components/widgets/custom-input/CustomInput";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import { useDispatch, useSelector } from "react-redux";
import { formatPhoneNumber, isEmpty } from "utility";
import { getPhoneTypeList, setSelectedPhoneType } from "redux/Common/action";
import { getRelationList } from "redux/EmergencyPlan/actions";
import { useHistory } from "react-router-dom";
import alerts from "constants/alerts";

const AddEmergencyContactModal = ({
  selectedContact,
  onSave,
  onClosePopup,
  onApiResponse,
}) => {
  const [phoneNumberState, setPhoneNumberState] = useState({
    phoneNumber: "",
    type: "",
    contactName: "",
    relation: "",
  });

  // Inline validation states (same as mobile pattern)
  const [fieldErrors, setFieldErrors] = useState({
    contactName: false,
    phoneNumber: false,
    relation: false,
    type: false,
  });
  const [validationTriggered, setValidationTriggered] = useState(false);

  const { phoneTypeList = [] } = useSelector(
    (store) => store?.common
  );
  const selectedPhoneType = useSelector(
    (store) => store?.common?.selectedPhoneType
  );
  const relationList = useSelector(
    (store) => store?.emergencyData?.relationList
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const userData = useSelector((store) => store?.auth?.userData);


  useEffect(() => {
    getPhoneTypeList({
      dispatch,
      history,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
    });
    getRelationList({
      dispatch,
      history,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
    });
  }, [dispatch, history, userData]);

  useEffect(() => {
    if (selectedContact) {
      setPhoneNumberState({
        phoneNumber: formatPhoneNumber(selectedContact?.phone || selectedContact?.phoneNumber),
        contactName: selectedContact?.name || "",
        relation: selectedContact?.relation || "",
        type: selectedContact?.primary_phone_type || "",
      });
      if (selectedContact?.primary_phone_type) {
        setSelectedPhoneType(dispatch, selectedContact?.primary_phone_type);
      }
    } else {
      // Reset form when no contact (adding new)
      setPhoneNumberState({
        phoneNumber: "",
        contactName: "",
        relation: "",
        type: "",
      });
      setSelectedPhoneType(dispatch, null);
    }
    // Reset validation when modal opens
    setValidationTriggered(false);
    setFieldErrors({
      contactName: false,
      phoneNumber: false,
      relation: false,
      type: false,
    });
  }, [selectedContact, dispatch]);

  // Update local state when Redux selectedPhoneType changes
  useEffect(() => {
    if (selectedPhoneType) {
      setPhoneNumberState((prev) => ({
        ...prev,
        type: selectedPhoneType,
      }));
    }
  }, [selectedPhoneType]);

    // Format relation list for dropdown
    const getAllRelationList = (relationList = []) => {
      return relationList?.map((ele) => {
        return {
          label: ele?.relation,
          value: ele?.relation,
          id: ele?.relation_id,
        };
      });
    };
  
    // Format phone type list for dropdown
    const getAllPhoneTypeListNames = (phoneTypeList = []) => {
      return phoneTypeList?.map((item) => ({
        id: item?.phone_type_id,
        label: item?.phone_type,
        phone_type: item?.phone_type,
      }));
    };
  
    // Handler functions for dropdown changes
    const handleRelationChange = (item) => {
      if (item && typeof item === 'object') {
        onValueChange("relation", { relation: item?.label, relation_id: item?.id });
      } else {
        onValueChange("relation", item);
      }
    };
  
    const handlePhoneTypeChange = (item) => {
      if (item && item?.label && item?.id) {
        const phoneTypeData = { phone_type: item?.label, phone_type_id: item?.id };
        setSelectedPhoneType(dispatch, phoneTypeData);
        onValueChange("type", phoneTypeData);
      }
    };
    
  const onValueChange = (type, value) => {
    setPhoneNumberState((prev) => {
      return { ...prev, [type]: value };
    });

    // Clear error for this field when user types (same as mobile)
    if (validationTriggered) {
      setFieldErrors((prev) => ({
        ...prev,
        [type]: false,
      }));
    }
  };

  const handlePhoneNumberChange = (inputVal) => {
    const formattedPhoneNumber = formatPhoneNumber(inputVal);
    onValueChange("phoneNumber", formattedPhoneNumber);
  };

  const onSaveChanges = () => {
    setValidationTriggered(true);

    // Clean phone number for validation
    const cleanPhone = phoneNumberState?.phoneNumber?.replace(/\D/g, "");

    // Validate all fields (same as mobile inline validation)
    const errors = {
      contactName: isEmpty(phoneNumberState?.contactName),
      relation: isEmpty(phoneNumberState?.relation),
      phoneNumber: isEmpty(phoneNumberState?.phoneNumber) || cleanPhone?.length < 10,
      type: isEmpty(phoneNumberState?.type),
    };

    setFieldErrors(errors);

    // Check if any errors exist
    const hasErrors = Object.values(errors).some(error => error);
    if (hasErrors) {
      // Show snackbar error if parent provided callback
      if (onApiResponse) {
        onApiResponse(alerts?.incorrect_or_missing_details, "error");
      }
      return;
    }

    // All valid - save and close
    onSave(phoneNumberState);
    setValidationTriggered(false);
    setFieldErrors({
      contactName: false,
      phoneNumber: false,
      relation: false,
      type: false,
    });
  };

  return (
    <div className="ep_emergency_contact_form">
      {/* Form Fields - 4 fields as per mobile */}
      <div className="ep_contact_form_fields">
        {/* 1. Emergency Contact Name */}
        <div className="ep_char_limit_field">
          <CustomInput
            placeholder={titles?.emergency_contact_name}
            className="ep_contact_input"
            required
            value={phoneNumberState?.contactName}
            onChange={(e) => {
              onValueChange("contactName", e?.target?.value);
            }}
            maxLength={40}
            error={validationTriggered && fieldErrors?.contactName}
            errorMessage={validationTriggered && fieldErrors?.contactName ? alerts?.incorrect_details : ""}
          />
          <div className="ep_char_count">
            {40 - (phoneNumberState?.contactName?.length || 0)} characters remaining
          </div>
        </div>
        
        {/* 2. Relation to Subscriber */}
        <CustomSelectBox
          title={titles?.relation_to_the_subscriber}
          labelClassName="ep_field_label"
          className="ep_contact_select"
          required={true}
          selectedValue={phoneNumberState?.relation?.relation || phoneNumberState?.relation}
          onChange={handleRelationChange}
          options={getAllRelationList(relationList)}
          keyValue={"relation"}
          error={validationTriggered && fieldErrors?.relation}
          errorMessage={validationTriggered && fieldErrors?.relation ? alerts?.incorrect_details : ""}
        />
        
        {/* 3. Phone Number */}
        <CustomInput
          placeholder="626-555-1965"
          className="ep_contact_input"
          required
          value={phoneNumberState?.phoneNumber}
          onChange={(e) => {
            handlePhoneNumberChange(e?.target?.value);
          }}
          error={validationTriggered && fieldErrors?.phoneNumber}
          errorMessage={validationTriggered && fieldErrors?.phoneNumber ? alerts?.incorrect_details : ""}
        />
        
        {/* 4. Select Phone Type */}
        <CustomSelectBox
          title={titles?.select_phone_type}
          labelClassName="ep_field_label"
          className="ep_contact_select"
          required={true}
          selectedValue={phoneNumberState?.type?.phone_type || phoneNumberState?.type}
          onChange={handlePhoneTypeChange}
          options={getAllPhoneTypeListNames(phoneTypeList)}
          keyValue={"phone_type"}
          error={validationTriggered && fieldErrors?.type}
          errorMessage={validationTriggered && fieldErrors?.type ? alerts?.incorrect_details : ""}
        />
        
        {/* Action Buttons */}
        <div className="ep_contact_buttons">
          <CustomButton
            text={titles?.save_changes}
            width="100%"
            onClick={onSaveChanges}
          />
          <CustomButton
            text={titles?.cancel}
            width="100%"
            onClick={onClosePopup}
            className="ep_cancel_btn"
          />
        </div>
      </div>
    </div>
  );
};

export default AddEmergencyContactModal;